<!-- seguridad -->
<template>
  <div v-if="tabs == 7">
    <div class="p-3">
      <h4 class="mb-4">
        {{ hasPassword ? "Cambiar Contraseña" : "Establecer Contraseña" }}
      </h4>

      <div v-if="isLoadingPasswordStatus" class="text-center py-4">
        <i class="fas fa-spinner fa-spin fa-2x"></i>
        <p class="mt-2">Cargando...</p>
      </div>

      <form v-else @submit.prevent="cambiarContrasena">
        <!-- Campo de contraseña actual - solo si tiene contraseña -->
        <div v-if="hasPassword" class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="currentPassword" class="form-label">
                Contraseña Actual <span class="text-danger">*</span>
              </label>
              <div class="position-relative">
                <input
                  v-model="formData.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  class="form-control pr-5"
                  id="currentPassword"
                  placeholder="Ingrese su contraseña actual"
                  required
                />
                <button
                  type="button"
                  class="btn-toggle-password"
                  @click="showCurrentPassword = !showCurrentPassword"
                  tabindex="-1"
                >
                  <i
                    :class="
                      showCurrentPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    "
                  ></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Alerta informativa - solo si no tiene contraseña y ya terminó de cargar -->
        <div
          v-if="!hasPassword && !isLoadingPasswordStatus"
          class="alert alert-info mb-3 d-flex align-items-center"
        >
          <i class="fas fa-info-circle mr-1"></i>
          No tienes una contraseña establecida. Por favor, establece una nueva
          contraseña para tu cuenta.
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="newPassword" class="form-label">
                {{ hasPassword ? "Nueva Contraseña" : "Contraseña" }}
                <span class="text-danger">*</span>
              </label>
              <div class="position-relative">
                <input
                  v-model="formData.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  class="form-control pr-5"
                  id="newPassword"
                  placeholder="Ingrese la nueva contraseña"
                  required
                  minlength="6"
                />
                <button
                  type="button"
                  class="btn-toggle-password"
                  @click="showNewPassword = !showNewPassword"
                  tabindex="-1"
                >
                  <i
                    :class="showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                  ></i>
                </button>
              </div>
              <small class="form-text text-muted">
                La contraseña debe tener al menos 6 caracteres
              </small>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="confirmPassword" class="form-label">
                Confirmar Nueva Contraseña <span class="text-danger">*</span>
              </label>
              <div class="position-relative">
                <input
                  v-model="formData.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control pr-5"
                  id="confirmPassword"
                  placeholder="Confirme la nueva contraseña"
                  required
                  minlength="6"
                />
                <button
                  type="button"
                  class="btn-toggle-password"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                >
                  <i
                    :class="
                      showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    "
                  ></i>
                </button>
              </div>
              <small
                v-if="
                  formData.newPassword &&
                  formData.confirmPassword &&
                  formData.newPassword !== formData.confirmPassword
                "
                class="form-text text-danger"
              >
                Las contraseñas no coinciden
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <vs-button type="submit" :disabled="!isFormValid || isSubmitting">
              <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin"></i> Guardando...
              </span>
              <span v-else>
                <i class="fas fa-save"></i>
                {{
                  hasPassword ? "Guardar Contraseña" : "Establecer Contraseña"
                }}
              </span>
            </vs-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "tab_seguridad",
  props: {
    tabs: {
      type: Number,
      required: true,
    },
    personaId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      isSubmitting: false,
      hasPassword: false,
      isLoadingPasswordStatus: true,
    };
  },
  async mounted() {
    if (this.personaId) {
      await this.checkIfUserHasPassword();
    }
  },
  watch: {
    personaId(newVal) {
      // Cuando personaId cambie de null a un valor válido, cargar los datos
      if (newVal && !this.hasPassword) {
        this.checkIfUserHasPassword();
      }
    },
  },
  computed: {
    isFormValid() {
      // Si el usuario tiene contraseña, debe ingresar la contraseña actual
      const currentPasswordValid = this.hasPassword
        ? this.formData.currentPassword
        : true;

      return (
        currentPasswordValid &&
        this.formData.newPassword &&
        this.formData.confirmPassword &&
        this.formData.newPassword === this.formData.confirmPassword &&
        this.formData.newPassword.length >= 6
      );
    },
  },
  methods: {
    async checkIfUserHasPassword() {
      // No hacer nada si no hay personaId
      if (!this.personaId) {
        this.isLoadingPasswordStatus = false;
        return;
      }

      try {
        this.isLoadingPasswordStatus = true;

        const data = await this.$store.dispatch("hl_get", {
          path: `Persona/GetPersonaById/${this.personaId}`,
        });

        // Verificar si el campo contrasena tiene valor (no null, no undefined, no cadena vacía)
        const password = data?.contrasena || data?.Contrasena;

        if (password && password.trim() !== "") {
          this.hasPassword = true;
        } else {
          this.hasPassword = false;
        }
      } catch (error) {
        console.error("Error al verificar contraseña:", error);
        // Por defecto asumimos que tiene contraseña por seguridad
        this.hasPassword = true;
      } finally {
        this.isLoadingPasswordStatus = false;
      }
    },
    async cambiarContrasena() {
      if (!this.isFormValid) {
        this.$toast.warning(
          "Por favor complete todos los campos correctamente"
        );
        return;
      }

      this.isSubmitting = true;

      try {
        // Si no tiene contraseña, enviar cadena vacía como contraseña actual
        const currentPwd = this.hasPassword
          ? this.formData.currentPassword
          : "";

        // Llamar al endpoint seguro que recibe datos en el body
        const data = await this.$store.dispatch("hl_post", {
          path: "Persona/ChangePasswordSecure",
          data: {
            PersonaId: this.personaId,
            CurrentPassword: currentPwd,
            NewPassword: this.formData.newPassword,
          },
        });

        if (data.status === "success") {
          this.$toast.success(
            data.msg || "Contraseña actualizada correctamente"
          );

          // Actualizar el estado de que ahora tiene contraseña
          this.hasPassword = true;

          // Limpiar el formulario
          this.formData = {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          };

          // Cerrar los ojos
          this.showCurrentPassword = false;
          this.showNewPassword = false;
          this.showConfirmPassword = false;
        } else {
          this.$toast.error(data.msg || "Error al actualizar la contraseña");
        }
      } catch (error) {
        console.error("Error al cambiar contraseña:", error);
        this.$toast.error(
          "Error al cambiar la contraseña. Por favor intente nuevamente."
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.btn-toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px 10px;
  color: #6c757d;
  transition: color 0.3s;
  z-index: 10;
}

.btn-toggle-password:hover {
  color: #4677d5;
}

.btn-toggle-password i {
  font-size: 1.2rem;
}
</style>
